import React, { useState } from 'react';
import { Check, Eye, Save, StickyNote, X } from 'lucide-react';
import { ClientDashboardAccess, ClientDashboardAccessPage } from '../../types/client.types';
import { CLIENT_PERMISSIONS_OPTIONS } from '../../types/client-permissions.types';
import { Button } from 'src/components/ui/button';
import { useCreateClientDashboardAccessMutation } from '../../endpoints/client.endpoints';
import notificationService from 'src/services/notification/notification.service';
import { useDispatch } from 'react-redux';
import { useActiveClient } from 'src/redux/active-client/use-active-client';

interface ManageClientPermissionsProps {
  permissions?: ClientDashboardAccess[];
  onChange?: (permissions: ClientDashboardAccess[]) => void;
  disabled?: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PermissionNode {
  page: string;
  tab: string;
  permission: string | null;
  id?: string;
}

const ManageClientPermissions: React.FC<ManageClientPermissionsProps> = ({
  permissions = [],
  onChange,
  disabled = false,
  onOpenChange,
}) => {
  const { activeClient } = useActiveClient();
  const dispatch = useDispatch();
  const [hasChanges, setHasChanges] = useState(false);
  const [createClientDashboardAccess] = useCreateClientDashboardAccessMutation();

  // Initialize state from permissions prop using lazy initializer
  const [localPermissions, setLocalPermissions] = useState<PermissionNode[]>(() =>
    permissions.map(p => ({
      page: p.page,
      tab: p.tab,
      permission: p.permission,
      id: p.id,
    }))
  );

  // Use ref to track previous permissions to avoid unnecessary updates
  const prevPermissionsRef = React.useRef<string>('');

  // Sync with external permissions prop
  React.useEffect(() => {
    const newPermissions = permissions.map(p => ({
      page: p.page,
      tab: p.tab,
      permission: p.permission,
      client: activeClient?.ext_client_id,
    }));

    // Create a stable comparison key for permissions
    const permissionsKey = JSON.stringify(newPermissions);

    // Only update if permissions actually changed
    if (prevPermissionsRef.current !== permissionsKey) {
      prevPermissionsRef.current = permissionsKey;
      setLocalPermissions(newPermissions);
    }
  }, [permissions]);

  // Check if a permission is selected
  const isSelected = (page: string, tab: string, permission: string | null): boolean => {
    return localPermissions.some(
      p => p.page === page && p.tab === tab && p.permission === permission
    );
  };

  // Check if a tab is selected (has at least one permission selected OR has a null permission entry)
  const isTabSelected = (page: string, tab: string): boolean => {
    return localPermissions.some(p => p.page === page && p.tab === tab);
  };

  // Check if a page is selected (has at least one tab selected)
  const isPageSelected = (page: string): boolean => {
    return localPermissions.some(p => p.page === page);
  };

  // Get selection state for display
  const getSelectionState = (page: string, tab: string, permission: string | null): 'checked' | 'unchecked' | 'indeterminate' => {
    if (permission !== null) {
      return isSelected(page, tab, permission) ? 'checked' : 'unchecked';
    }

    // For tabs, check if all permissions are selected or some are selected
    const pageData = CLIENT_PERMISSIONS_OPTIONS.find(p => p.value === page);
    const tabData = pageData?.tabs.find(t => t.value === tab);
    const tabPermissions = tabData?.permissions || [];

    // Check if tab is explicitly selected (has null permission entry)
    // This takes priority - if tab has null entry, it's always checked
    const isTabExplicitlySelected = localPermissions.some(
      p => p.page === page && p.tab === tab && p.permission === null
    );

    if (tabPermissions.length === 0) {
      // Tab with no permissions - just check if tab is selected
      return isTabSelected(page, tab) ? 'checked' : 'unchecked';
    }

    // If tab is explicitly selected (null permission entry), it's always checked
    // This ensures the tab stays selected even when permissions are deselected
    if (isTabExplicitlySelected) {
      const selectedPermissions = tabPermissions.filter(perm =>
        isSelected(page, tab, perm.value)
      );
      // If all permissions are selected, show as checked
      if (selectedPermissions.length === tabPermissions.length) {
        return 'checked';
      }
      // If some permissions are selected, show as indeterminate
      if (selectedPermissions.length > 0) {
        return 'indeterminate';
      }
      // If no permissions are selected but tab has null entry, still show as checked
      return 'checked';
    }

    // Tab is not explicitly selected, check permissions
    const selectedPermissions = tabPermissions.filter(perm =>
      isSelected(page, tab, perm.value)
    );

    if (selectedPermissions.length === 0) {
      return 'unchecked';
    }
    if (selectedPermissions.length === tabPermissions.length) {
      return 'checked';
    }
    return 'indeterminate';
  };

  // Helper to update permissions and call onChange
  const updatePermissions = (newPermissions: PermissionNode[]) => {
    setLocalPermissions(newPermissions);
    onChange?.(newPermissions.map(p => ({
      id: p.id,
      client: '',
      page: p.page,
      tab: p.tab,
      permission: p.permission,
    })));
  };

  // Helper to find and select items by their value (for requires)
  // Only selects the item itself, not all permissions of a tab
  const selectByValue = (value: string, newPermissions: PermissionNode[]): void => {
    // Search through all pages, tabs, and permissions to find matching values
    for (const page of CLIENT_PERMISSIONS_OPTIONS) {
      // Check if page value matches
      if (page.value === value) {
        // Select entire page (all tabs with null permission entries)
        for (const tab of page.tabs) {
          if (!newPermissions.some(p => p.page === page.value && p.tab === tab.value && p.permission === null)) {
            newPermissions.push({
              page: page.value,
              tab: tab.value,
              permission: null,
            });
          }
        }
        return;
      }

      // Check tabs
      for (const tab of page.tabs) {
        if (tab.value === value) {
          // Select just the tab itself (null permission entry), NOT all permissions
          if (!newPermissions.some(p => p.page === page.value && p.tab === tab.value && p.permission === null)) {
            newPermissions.push({
              page: page.value,
              tab: tab.value,
              permission: null,
            });
          }
          return;
        }

        // Check permissions
        for (const perm of tab.permissions) {
          if (perm.value === value) {
            // Select just this permission
            if (!newPermissions.some(p => p.page === page.value && p.tab === tab.value && p.permission === perm.value)) {
              newPermissions.push({
                page: page.value,
                tab: tab.value,
                permission: perm.value,
              });
            }
            return;
          }
        }
      }
    }
  };

  // Handle permission toggle
  const handlePermissionToggle = (page: string, tab: string, permission: string | null) => {
    setHasChanges(true);
    if (disabled) return;

    const isCurrentlySelected = isSelected(page, tab, permission);

    if (isCurrentlySelected) {
      // Deselecting: Remove just this permission (ignore required)
      // IMPORTANT: Keep the null permission entry for the tab so it stays selected
      const newPermissions = localPermissions.filter(
        p => !(p.page === page && p.tab === tab && p.permission === permission)
      );

      // Ensure the tab's null permission entry remains (if it exists)
      // This keeps the tab selected even when all permissions are deselected
      const hasNullEntry = localPermissions.some(
        p => p.page === page && p.tab === tab && p.permission === null
      );
      if (hasNullEntry && !newPermissions.some(p => p.page === page && p.tab === tab && p.permission === null)) {
        const existingFromOriginal = localPermissions.find(
          p => p.page === page && p.tab === tab && p.permission === null
        );
        newPermissions.push({
          page,
          tab,
          permission: null,
          id: existingFromOriginal?.id,
        });
      }

      updatePermissions(newPermissions);
    } else {
      // Selecting: Add this permission, parent tab, parent page, and any required items
      const newPermissions: PermissionNode[] = [...localPermissions];

      // Add this permission
      if (!newPermissions.some(p => p.page === page && p.tab === tab && p.permission === permission)) {
        const existingFromOriginal = localPermissions.find(
          p => p.page === page && p.tab === tab && p.permission === permission
        );
        newPermissions.push({
          page,
          tab,
          permission,
          id: existingFromOriginal?.id,
        });
      }

      // Ensure parent tab is explicitly selected (add null permission entry to mark it as selected)
      // This allows the tab to remain selected even if all permissions are later deselected
      const pageData = CLIENT_PERMISSIONS_OPTIONS.find(p => p.value === page);
      const tabData = pageData?.tabs.find(t => t.value === tab);

      if (tabData) {
        // Add null permission entry to mark the tab as explicitly selected
        // This works for both tabs with and without permissions
        if (!newPermissions.some(p => p.page === page && p.tab === tab && p.permission === null)) {
          const existingFromOriginal = localPermissions.find(
            p => p.page === page && p.tab === tab && p.permission === null
          );
          newPermissions.push({
            page,
            tab,
            permission: null,
            id: existingFromOriginal?.id,
          });
        }
      }

      // The page is automatically selected when it has tabs with permissions
      // (handled by isPageSelected)

      // Handle requires:
      // 1. Check if the permission itself has requires
      if (permission !== null && tabData) {
        const permissionData = tabData.permissions.find(p => p.value === permission);
        if (permissionData?.requires) {
          for (const requiredValue of permissionData.requires) {
            selectByValue(requiredValue, newPermissions);
          }
        }
      }

      // 2. Check if the parent tab has requires (select only the required items, not all permissions)
      if (tabData?.requires) {
        for (const requiredValue of tabData.requires) {
          selectByValue(requiredValue, newPermissions);
        }
      }

      updatePermissions(newPermissions);
    }
  };

  // Handle tab toggle
  const handleTabToggle = (page: string, tab: string) => {
    setHasChanges(true);
    if (disabled) return;

    const pageData = CLIENT_PERMISSIONS_OPTIONS.find(p => p.value === page);
    const tabData = pageData?.tabs.find(t => t.value === tab);
    if (!tabData) return;

    const isCurrentlySelected = isTabSelected(page, tab);

    if (isCurrentlySelected) {
      // Deselecting: Remove all permissions for this tab (ignore required)
      const newPermissions = localPermissions.filter(
        p => !(p.page === page && p.tab === tab)
      );
      updatePermissions(newPermissions);
    } else {
      // Selecting: Add all permissions for this tab, and any required items
      const newPermissions: PermissionNode[] = [];

      // Add existing permissions that aren't for this tab
      newPermissions.push(...localPermissions.filter(
        p => !(p.page === page && p.tab === tab)
      ));

      // Preserve IDs for existing permissions in this tab
      const existingTabPermissions = localPermissions.filter(
        p => p.page === page && p.tab === tab
      );

      // Add all permissions for this tab
      if (tabData.permissions.length === 0) {
        // Tab with no permissions - add null permission entry
        const existingTab = existingTabPermissions.find(p => p.permission === null);
        newPermissions.push({
          page,
          tab,
          permission: null,
          id: existingTab?.id,
        });
      } else {
        // Tab with permissions - add all permissions
        tabData.permissions.forEach(perm => {
          const existing = existingTabPermissions.find(
            p => p.permission === perm.value
          );
          newPermissions.push({
            page,
            tab,
            permission: perm.value,
            id: existing?.id,
          });
        });
      }

      // Handle requires: select required items
      if (tabData.requires) {
        for (const requiredValue of tabData.requires) {
          selectByValue(requiredValue, newPermissions);
        }
      }

      updatePermissions(newPermissions);
    }
  };

  // Handle page toggle
  const handlePageToggle = (page: string) => {
    setHasChanges(true);
    if (disabled) return;

    const pageData = CLIENT_PERMISSIONS_OPTIONS.find(p => p.value === page);
    if (!pageData) return;

    const isCurrentlySelected = isPageSelected(page);

    if (isCurrentlySelected) {
      // Deselecting: Remove all permissions and tabs for this page (ignore required)
      const newPermissions = localPermissions.filter(p => p.page !== page);
      updatePermissions(newPermissions);
    } else {
      // Selecting: Add all tabs and permissions for this page, and any required items
      const newPermissions: PermissionNode[] = [];

      // Add existing permissions that aren't for this page
      newPermissions.push(...localPermissions.filter(p => p.page !== page));

      // Preserve IDs for existing permissions in this page
      const existingPagePermissions = localPermissions.filter(p => p.page === page);

      // Add all tabs for this page
      pageData.tabs.forEach(tab => {
        if (tab.permissions.length === 0) {
          // Tab with no permissions - add null permission entry
          const existingTab = existingPagePermissions.find(
            p => p.tab === tab.value && p.permission === null
          );
          newPermissions.push({
            page,
            tab: tab.value,
            permission: null,
            id: existingTab?.id,
          });
        } else {
          // Tab with permissions - add all permissions
          tab.permissions.forEach(perm => {
            const existing = existingPagePermissions.find(
              p => p.tab === tab.value && p.permission === perm.value
            );
            newPermissions.push({
              page,
              tab: tab.value,
              permission: perm.value,
              id: existing?.id,
            });
          });
        }
      });

      // Handle requires: select required items
      if (pageData.requires) {
        for (const requiredValue of pageData.requires) {
          selectByValue(requiredValue, newPermissions);
        }
      }

      updatePermissions(newPermissions);
    }
  };

  const handleSave = () => {
    try {
      if (!activeClient?.ext_client_id) return;
      createClientDashboardAccess(localPermissions.map(p => ({
        client: activeClient.ext_client_id,
        page: p.page,
        tab: p.tab,
        permission: p.permission,
      })));
      onOpenChange(false);
      notificationService.handleSuccess(null, dispatch, 'Client permissions saved successfully');
    } catch (error) {
      notificationService.handleError(error, dispatch, 'Error saving client permissions');
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const getNodeBgColor = (page: string, tab?: string, permission?: string | null) => {
    if (permission !== undefined) {
      const state = getSelectionState(page, tab || '', permission);
      if (state === 'checked') {
        return 'bg-green-501/60 text-white hover:bg-green-501/50';
      }
      return 'bg-gray-200/30 hover:bg-gray-201/50';
    }
    if (tab !== undefined) {
      const state = getSelectionState(page, tab, null);
      if (state === 'checked') {
        return 'bg-green-501/80 text-white hover:bg-green-501/70';
      }
      if (state === 'indeterminate') {
        return 'bg-green-501/40 text-white hover:bg-green-501/30';
      }
      return 'bg-gray-200 hover:bg-gray-201/60';
    }
    // Page level
    const isSelected = isPageSelected(page);
    if (isSelected) {
      return 'bg-green-501 text-white hover:bg-green-501/90';
    }
    return 'bg-gray-201 hover:bg-gray-400';
  };

  const renderTreeNode = (page: ClientDashboardAccessPage, level: number = 0, index: number = 0) => {
    const paddingLeft = level * 30;

    return (
      <div key={page.value + level + index}>
        {/* Page level */}
        <div
          className={`flex items-center gap-2 cursor-pointer px-2 py-2 ${getNodeBgColor(page.value)} ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          style={{ paddingLeft: `${paddingLeft + 8}px` }}
          onClick={() => !disabled && handlePageToggle(page.value)}
        >
          <StickyNote className="w-4 h-4" />
          <span className="flex-1 font-bold text-sm">
            {page.name}
          </span>
          {isPageSelected(page.value) && (
            <Check className="w-4 h-4" />
          )}
        </div>

        {/* Tab level */}
        {page.tabs.map((tab, tabIndex) => (
          <div key={`${page.value}-${tab.value}-${tabIndex}`}>
            <div
              className={`flex items-center gap-2 cursor-pointer px-2 py-2 ${getNodeBgColor(page.value, tab.value)} ${
                disabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{ paddingLeft: `${(level + 1) * 30 + 8}px` }}
              onClick={() => !disabled && handleTabToggle(page.value, tab.value)}
            >
              <Eye className="w-4 h-4" />
              <span className="flex-1 font-semibold text-sm">
                {tab.name}
              </span>
              {(() => {
                const state = getSelectionState(page.value, tab.value, null);
                if (state === 'checked') {
                  return <Check className="w-4 h-4" />;
                }
                if (state === 'indeterminate') {
                  return <Check className="w-4 h-4" />;
                }
                // Also show checkmark if tab has any permissions selected
                if (isTabSelected(page.value, tab.value)) {
                  return <Check className="w-4 h-4" />;
                }
                return null;
              })()}
            </div>

            {/* Permission level */}
            {tab.permissions.map((permission, permissionIndex) => {
              const state = getSelectionState(page.value, tab.value, permission.value);
              return (
                <div
                  key={`${page.value}-${tab.value}-${permission.value}-${permissionIndex}`}
                  className={`flex items-center gap-2 cursor-pointer px-2 py-2 ${getNodeBgColor(page.value, tab.value, permission.value)} ${
                    disabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  style={{ paddingLeft: `${(level + 2) * 30 + 8}px` }}
                  onClick={() => !disabled && handlePermissionToggle(page.value, tab.value, permission.value)}
                >
                  <span className="flex-1 font-normal text-sm">
                    {permission.name}
                  </span>
                  {state === 'checked' && (
                    <Check className="w-4 h-4" />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full relative">
      <div className="border border-gray-300 rounded-md bg-white max-h-[600px] min-h-[400px] overflow-y-auto">
        {CLIENT_PERMISSIONS_OPTIONS.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            No permissions available
          </div>
        ) : (
          <div className="h-full">
            {CLIENT_PERMISSIONS_OPTIONS.map((page, index) => renderTreeNode(page, 0, index))}
          </div>
        )}
      </div>
      <div
        className={`sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-10 p-4 justify-end gap-2 text-right`}
      >
        <Button
          variant='ghost'
          className='text-gray-700 hover:bg-gray-201 bg-gray-200 mr-2'
          size='med'
          onClick={handleCancel}
        >
          <X className='w-4 h-4' />
          Cancel
        </Button>
        <Button
          variant='ghost'
          className='text-gray-700 hover:bg-gray-201 bg-green-501 text-white'
          size='med'
          onClick={handleSave}
          disabled={!hasChanges}
        >
          <Save className='w-4 h-4' />
          Save
        </Button>
      </div>
    </div>
  );
};

export default ManageClientPermissions;
